import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface AttributionWindowSelectorProps {
  campaignId: number;
  currentWindow: number | null;
  scheduledAt: string;
  onWindowUpdate: (newWindow: number | null) => void;
}

export const AttributionWindowSelector: React.FC<AttributionWindowSelectorProps> = ({
  campaignId,
  currentWindow,
  scheduledAt,
  onWindowUpdate
}) => {
  const [selectedType, setSelectedType] = useState<string>(
    currentWindow === null ? 'all_time' : currentWindow === 7 ? 'seven_days' : 'custom'
  );
  const [customDays, setCustomDays] = useState<number>(currentWindow && currentWindow !== 7 ? currentWindow : 30);
  const [isUpdating, setIsUpdating] = useState(false);

  const getDisplayWindow = () => {
    if (currentWindow === null) return 'All Time';
    return `${currentWindow} days`;
  };

  const getDateRange = () => {
    const startDate = new Date(scheduledAt);
    const startStr = startDate.toLocaleDateString();
    
    if (currentWindow === null) {
      return `${startStr} - Ongoing`;
    }
    
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + currentWindow);
    return `${startStr} - ${endDate.toLocaleDateString()}`;
  };

  const handleSave = async () => {
    setIsUpdating(true);
    
    try {
      let newWindow: number | null;
      
      switch (selectedType) {
        case 'seven_days':
          newWindow = 7;
          break;
        case 'custom':
          newWindow = customDays;
          break;
        case 'all_time':
          newWindow = null;
          break;
        default:
          newWindow = 7;
      }

      const { data, error } = await supabase.rpc('update_campaign_attribution_window', {
        p_campaign_id: campaignId,
        p_attribution_window_days: newWindow
      });

      if (error) throw error;

      if (data?.success) {
        onWindowUpdate(newWindow);
        toast({
          title: "Attribution Window Updated",
          description: `Attribution window set to ${newWindow === null ? 'All Time' : `${newWindow} days`}`
        });
      } else {
        throw new Error(data?.error || 'Failed to update attribution window');
      }
    } catch (error) {
      console.error('Error updating attribution window:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update attribution window. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Attribution Window Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">
              Current Window: {getDisplayWindow()}
            </Label>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground">
              Date Range: {getDateRange()}
            </Label>
          </div>
        </div>

        <div className="space-y-4">
          <Label className="text-sm font-medium">Select Attribution Window</Label>
          
          <RadioGroup value={selectedType} onValueChange={setSelectedType}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="seven_days" id="seven_days" />
              <Label htmlFor="seven_days" className="text-sm">
                7 days (default)
              </Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="custom" id="custom" />
              <Label htmlFor="custom" className="text-sm">
                Custom days
              </Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="all_time" id="all_time" />
              <Label htmlFor="all_time" className="text-sm">
                All time (from campaign sent date)
              </Label>
            </div>
          </RadioGroup>

          {selectedType === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="custom-days" className="text-sm">
                Number of days
              </Label>
              <Input
                id="custom-days"
                type="number"
                min="1"
                max="365"
                value={customDays}
                onChange={(e) => setCustomDays(parseInt(e.target.value) || 1)}
                className="w-32"
              />
            </div>
          )}

          <Button 
            onClick={handleSave} 
            disabled={isUpdating}
            className="w-full"
          >
            {isUpdating ? 'Updating...' : 'Save Attribution Window'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};